import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useFetch } from '@/modules/core/composables/useFetch'

export interface Note {
  id: number
  text: string
  author_name: string
  author_id: number
  permission: 'everyone' | 'investors' | 'admins'
  permission_label: string
  is_edited: boolean
  created_at: string
  updated_at: string
  replies: Note[]
}

export const useNoteStore = defineStore('investing/noteStore', () => {
  const notes = ref<Note[]>([])
  const loading = ref(false)

  const fetchNotes = async (commentableType: string, commentableId: string, { slug }: { slug: string }) => {
    loading.value = true
    try {
      const data = await useFetch(`/v4/${slug}/investing/${commentableType}/${commentableId}/notes`).get<Note[]>()
      notes.value = data
    } finally {
      loading.value = false
    }
  }

  const createNote = async (
    commentableType: string,
    commentableId: string,
    payload: { text: string; permission?: string },
    { slug }: { slug: string },
  ) => {
    const data = await useFetch(`/v4/${slug}/investing/${commentableType}/${commentableId}/notes`).post<Note>(payload)
    notes.value.unshift(data)
    return data
  }

  const updateNote = async (noteId: number, payload: { text: string }, { slug }: { slug: string }) => {
    const data = await useFetch(`/v4/${slug}/investing/notes/${noteId}/update`).post<Note>(payload)

    const index = notes.value.findIndex((n) => n.id === noteId)
    if (index !== -1) {
      notes.value[index] = data
    }

    // Also check replies
    for (const note of notes.value) {
      const replyIndex = note.replies?.findIndex((r) => r.id === noteId)
      if (replyIndex !== undefined && replyIndex !== -1) {
        note.replies[replyIndex] = data
        break
      }
    }

    return data
  }

  const deleteNote = async (noteId: number, { slug }: { slug: string }) => {
    await useFetch(`/v4/${slug}/investing/notes/${noteId}/remove`).post()

    const index = notes.value.findIndex((n) => n.id === noteId)
    if (index !== -1) {
      notes.value.splice(index, 1)
      return
    }

    // Also check replies
    for (const note of notes.value) {
      const replyIndex = note.replies?.findIndex((r) => r.id === noteId)
      if (replyIndex !== undefined && replyIndex !== -1) {
        note.replies.splice(replyIndex, 1)
        break
      }
    }
  }

  return {
    notes,
    loading,
    fetchNotes,
    createNote,
    updateNote,
    deleteNote,
  }
})
